/**
 * A coluna dos servidores. O único sítio onde o utilizador troca de contexto,
 * por isso carrega os dois sinais que decidem para onde ele vai a seguir:
 * a barra branca (activo) e o ponto/badge (por ler / menções).
 */

import { Limits } from "@greenvoice/shared";
import { useState } from "react";
import { api } from "../lib/api";
import { serverHasUnread, serverList, serverMentions } from "../store/selectors";
import { useStore } from "../store/store";
import { IconPlus } from "./Icons";

export function ServerRail() {
  const state = useStore();
  const servers = serverList(state);
  const [creating, setCreating] = useState(false);

  return (
    <nav className="rail" aria-label="Servidores">
      <button
        type="button"
        className={`rail__item rail__home${state.activeServerId === null ? " is-active" : ""}`}
        onClick={() => state.selectServer(null)}
        title="Mensagens diretas"
      >
        GV
      </button>

      <span className="rail__divider" />

      {servers.map((server) => {
        const active = state.activeServerId === server.id;
        const mentions = serverMentions(state, server.id);
        const unread = mentions > 0 || serverHasUnread(state, server.id);

        return (
          <button
            key={server.id}
            type="button"
            className={`rail__item${active ? " is-active" : ""}${unread ? " is-unread" : ""}`}
            onClick={() => state.selectServer(server.id)}
            title={server.name}
          >
            {server.iconUrl ? (
              <img src={server.iconUrl} alt="" width={44} height={44} />
            ) : (
              <span className="rail__initials">{initials(server.name)}</span>
            )}
            {mentions > 0 ? <span className="rail__badge">{mentions > 99 ? "99+" : mentions}</span> : null}
          </button>
        );
      })}

      <button type="button" className="rail__item rail__add" onClick={() => setCreating(true)} title="Criar servidor">
        <IconPlus size={22} />
      </button>

      {creating ? <CreateServerDialog onClose={() => setCreating(false)} /> : null}
    </nav>
  );
}

function initials(name: string): string {
  return name
    .split(/\s+/)
    .slice(0, 2)
    .map((word) => word[0] ?? "")
    .join("")
    .toUpperCase();
}

function CreateServerDialog({ onClose }: { onClose: () => void }) {
  const [name, setName] = useState("");
  const [code, setCode] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const selectServer = useStore((s) => s.selectServer);

  async function run(action: () => Promise<{ id: string }>) {
    setBusy(true);
    setError(null);
    try {
      const server = await action();
      // O `ServerCreate` do gateway chega a seguir e preenche canais e roles;
      // seleccionar já aqui evita o ecrã em branco entre as duas coisas.
      selectServer(server.id);
      onClose();
    } catch (caught) {
      setError((caught as Error).message);
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="modal" role="dialog" aria-modal="true" aria-label="Criar ou entrar num servidor">
      <div className="modal__panel" onClick={(e) => e.stopPropagation()}>
        <h2>Novo servidor</h2>
        <p className="modal__hint">Crie um espaço para a sua equipe ou entre com um convite.</p>

        <label className="field">
          <span>Nome</span>
          <input
            value={name}
            onChange={(e) => setName(e.target.value)}
            minLength={Limits.serverName.min}
            maxLength={Limits.serverName.max}
            placeholder="Equipe Green"
            autoFocus
          />
        </label>
        <button
          type="button"
          className="button button--primary"
          disabled={busy || name.trim().length < Limits.serverName.min}
          onClick={() => void run(() => api.servers.create({ name: name.trim() }))}
        >
          Criar
        </button>

        <div className="modal__sep">ou</div>

        <label className="field">
          <span>Código de convite</span>
          <input
            value={code}
            onChange={(e) => setCode(e.target.value)}
            placeholder="a1b2c3d4"
            maxLength={Limits.inviteCodeLength}
          />
        </label>
        <button
          type="button"
          className="button"
          disabled={busy || !code.trim()}
          onClick={() => void run(() => api.invites.join(code.trim()))}
        >
          Entrar
        </button>

        {error ? (
          <p className="auth__error" role="alert">
            {error}
          </p>
        ) : null}

        <button type="button" className="link modal__close" onClick={onClose}>
          Cancelar
        </button>
      </div>
    </div>
  );
}
